import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Trash2, ExternalLink, Image as ImageIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import MediaPicker from "@/components/MediaPicker";
import RichTextEditor from "@/components/RichTextEditor";

function Text({ label, value, onChange, testid, help, type = "text" }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (parseInt(e.target.value, 10) || 0) : e.target.value)} data-testid={testid} className="input-line mt-3" />
      {help && <p className="mt-2 text-[12px] text-ink/50 italic">{help}</p>}
    </div>
  );
}
function Area({ label, value, onChange, testid, rows = 3 }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={rows} className="input-line mt-3 resize-y" />
    </div>
  );
}
function ImagePick({ label, value, onChange, testid, help }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <div className="mt-3 flex items-start gap-4">
        <div className="w-32 h-32 bg-mist/40 border border-mist flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={22} className="text-ink/30" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-3">
            <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-pick`} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper">Choose from library</button>
            {value && <button type="button" onClick={() => onChange("")} className="text-[12px] tracking-[0.14em] uppercase text-ink/50 hover:text-copper inline-flex items-center gap-1"><X size={12} /> Clear</button>}
          </div>
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste an image URL or pick from library" data-testid={testid} className="input-line text-[13px]" />
          {help && <p className="text-[12px] text-ink/50 italic">{help}</p>}
        </div>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onPick={onChange} />
    </div>
  );
}

function TagsField({ value, onChange }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">Tags (comma separated)</label>
      <input
        type="text"
        data-testid="journal-tags"
        value={(value || []).join(", ")}
        onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        className="input-line mt-3"
      />
    </div>
  );
}

const EMPTY = {
  slug: "", title: "", status: "draft", order: 0,
  published_at: new Date().toISOString().slice(0, 10),
  author: "", category: "", region: "",
  excerpt: "", hero_image: "", card_image: "",
  body: "", tags: [],
  seo: { title: "", description: "", og_title: "", og_description: "" },
};

export default function JournalEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = slug === "new";
  const [form, setForm] = useState(isNew ? { ...EMPTY } : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/journal/${slug}`).then(r => setForm({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => { toast.error("Not found"); navigate("/studio/journal", { replace: true }); });
    }
  }, [slug, isNew, navigate]);

  if (!form) return <p className="text-ink/50 text-sm">Loading…</p>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const setSeo = (k) => (v) => setForm({ ...form, seo: { ...form.seo, [k]: v } });

  const save = async (e) => {
    e?.preventDefault();
    if (!form.title?.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      let r;
      if (isNew) r = await api.post("/admin/journal", form);
      else r = await api.put(`/admin/journal/${slug}`, form);
      toast.success("Saved.");
      if (isNew || r.data.slug !== slug) navigate(`/studio/journal/${r.data.slug}`, { replace: true });
      else setForm({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/journal/${slug}`);
      toast.success("Deleted.");
      navigate("/studio/journal", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid={`journal-edit-${slug}`}>
      <Link to="/studio/journal" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ink/60 hover:text-copper">
        <ChevronLeft size={14} /> Back to journal
      </Link>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">{isNew ? "New journal post" : form.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && form.status === "published" && (
            <a target="_blank" rel="noopener noreferrer" href={`/journal/${form.slug}`} className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
              <ExternalLink size={14} /> Preview
            </a>
          )}
          <select data-testid="journal-status-select" value={form.status || "draft"} onChange={(e) => set("status")(e.target.value)} className="border border-mist px-3 py-2 text-[13px] bg-ivory">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      <form onSubmit={save} className="mt-12 space-y-14">
        <section className="space-y-6">
          <p className="eyebrow text-copper">Basics</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Text label="Post title" value={form.title} onChange={set("title")} testid="journal-title" />
            <Text label="Slug (URL)" value={form.slug} onChange={set("slug")} testid="journal-slug" help="Leave blank to auto-generate." />
            <Text label="Published date" value={form.published_at} onChange={set("published_at")} testid="journal-date" type="date" />
            <Text label="Display order" value={form.order} onChange={set("order")} testid="journal-order" type="number" />
            <Text label="Author" value={form.author} onChange={set("author")} testid="journal-author" />
            <Text label="Category" value={form.category} onChange={set("category")} testid="journal-category" help="e.g. Ritual, Field notes, Behind the scenes" />
            <Text label="Region / destination" value={form.region} onChange={set("region")} testid="journal-region" help="Free text or a destination slug" />
          </div>
          <TagsField value={form.tags} onChange={set("tags")} />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Images</p>
          <ImagePick label="Hero image" value={form.hero_image} onChange={set("hero_image")} testid="journal-hero-image" />
          <ImagePick label="Card image" value={form.card_image} onChange={set("card_image")} testid="journal-card-image" help="Falls back to hero image if blank." />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Story</p>
          <Area label="Excerpt (shown on cards)" value={form.excerpt} onChange={set("excerpt")} testid="journal-excerpt" rows={2} />
          <div>
            <label className="eyebrow text-ink/55">Body</label>
            <div className="mt-3">
              <RichTextEditor value={form.body} onChange={set("body")} testid="journal-body" />
            </div>
            <p className="mt-2 text-[12px] text-ink/50 italic">Use headings, lists, blockquotes and links. Existing posts have been auto-converted from plain text on first load.</p>
          </div>
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">SEO &amp; social</p>
          <Text label="Page title" value={form.seo?.title} onChange={setSeo("title")} testid="journal-seo-title" />
          <Area label="Meta description" value={form.seo?.description} onChange={setSeo("description")} testid="journal-seo-desc" rows={2} />
          <Text label="Open Graph title" value={form.seo?.og_title} onChange={setSeo("og_title")} testid="journal-seo-og-title" />
          <Area label="Open Graph description" value={form.seo?.og_description} onChange={setSeo("og_description")} testid="journal-seo-og-desc" rows={2} />
        </section>

        <div className="pt-4 flex justify-between gap-3">
          {!isNew && (
            <button type="button" onClick={remove} data-testid="journal-delete" className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
              <Trash2 size={14} /> Delete post
            </button>
          )}
          <div className="ml-auto flex gap-3">
            <Link to="/studio/journal" className="btn-secondary">Cancel</Link>
            <button data-testid="journal-save" disabled={saving} type="submit" className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
